import React, { ReactNode, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { ArrowForward } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { Button } from '../../Button';
import { PercentageChip } from './PercentageChip.component';


const useStyles = makeStyles<Partial<ProgressChartHeaderProps>>()(theme => ({
  root: {
    display: 'flex',
    alignItems: 'space-between',
    justifyContent: 'center',
    padding: theme.spacing(3, 0, 2, 0),
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  titleLabel: {
    lineHeight: 1,
    paddingBottom: '4px',
  },
  titleRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  titleValue: {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 1,
  }
}));


export type ProgressChartHeaderProps = {
  title: string;
  value: number | string;
  customTitleValue?: ReactNode;
  disabled?: boolean;
  difference?: number;
  onDetails?: () => void;
  classes?: Partial<typeof useStyles>;
};


export const ProgressChartHeader: React.FC<ProgressChartHeaderProps> = (props) => {
  const l10n = useL10n();
  const { classes } = useStyles(props, { props });

  const titleValue = useMemo(() => {
    if (props.customTitleValue) {
      return props.customTitleValue;
    }
    return (
      <Box className={classes.titleValue}>
        {props.value}
      </Box>
    );
  }, [ classes.titleValue, props.customTitleValue, props.value ]);

  return (
    <Box className={classes.root}>
      <Box className={classes.title}>
        <Typography variant="body2" color="textPrimary" className={classes.titleLabel}>
          {props.title}
        </Typography>
        <Box className={classes.titleRoot}>
          {titleValue}
          {typeof props.difference === 'number' ? (
            <PercentageChip value={props.difference} />
          ) : null}
        </Box>
      </Box>
      {props.onDetails && (
        <Box>
          <Button
            onClick={props.onDetails}
            disabled={props.disabled}
            endIcon={<ArrowForward />}
            size="small"
          >
            {l10n.shared.charts.ProgressChart.details}
          </Button>
        </Box>
      )}
    </Box>
  );
};


export const ProgressChartHeaderVertical = withStyles(ProgressChartHeader, {
  titleRoot: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  }
});
